// assets/add-ons-drawer.js
// Phase 3: merchant-configurable blocks (zero-items guard)
// Phase 2: cart state fetch, checkbox sync, smart-skip, running total, cart mutations
// Phase 1: form submit intercept + drawer open/close

class AddOnsDrawer extends HTMLElement {
  #drawer = null;
  #cartTotal = 0; // cart base total in cents, fetched on #open()
  #queue = Promise.resolve(); // Promise chain anchor — ensures one Cart API mutation at a time
  #items = []; // cached item element references — survives x-drawer shadow DOM reparenting

  connectedCallback() {
    this.#drawer = this.querySelector('#add-ons-drawer');

    // Cache item elements NOW (before x-drawer opens and moves them to shadow DOM).
    // querySelectorAll works here because connectedCallback fires while elements
    // are still in light DOM.
    this.#items = [...this.querySelectorAll('.add-ons-drawer__item')];

    // Intercept cart drawer checkout via event delegation on document.
    // Must NOT bind to the form element directly — CartDrawer.replaceChildren()
    // destroys the old form after cart:refresh, killing any direct listener.
    document.addEventListener('submit', (e) => {
      if (e.target.closest('.cart-drawer__footer') && e.submitter?.name === 'checkout') {
        this.#handleCheckout(e);
      }
    });

    // Wire Skip button — close drawer without modifying cart
    const skipBtn = this.querySelector('[data-action="skip"]');
    if (skipBtn) {
      skipBtn.addEventListener('click', () => {
        this.#close();
        window.location.href = '/checkout';
      });
    }

    // Wire Add to Cart CTA — mutate cart, then proceed to /checkout.
    // CRITICAL (DRWR-06): On success this handler MUST navigate to /checkout. The
    // entire purpose of this drawer is to commit optional add-ons en route to checkout;
    // if we don't redirect, the user is stranded back in the cart drawer and the
    // checkout button effectively does nothing.
    //
    // We deliberately do NOT dispatch cart:change with baseEvent:'variant:add' here:
    // the theme's CartDrawer reacts to that event by re-opening itself (theme.js
    // line 4223 — onCartChange_fn calls this.show()), which would flash the cart
    // drawer open on top of the navigation. /checkout refetches the cart server-side,
    // so we don't need to re-render the client-side cart drawer the user is leaving.
    const checkoutBtn = this.querySelector('[data-action="checkout"]');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', async () => {
        checkoutBtn.setAttribute('aria-busy', 'true');
        checkoutBtn.disabled = true;
        try {
          // #mutateCart() is a no-op if the user has selected nothing AND has no
          // existing add-ons to remove — that's fine, we still want to advance them
          // to /checkout (the user explicitly clicked the primary CTA).
          await this.#mutateCart();
          // Navigate immediately. Do NOT close the drawer first — the page is about
          // to unload, and closing triggers an animation that would just delay
          // navigation visually. See top-of-handler note on why no cart:change dispatch.
          window.location.href = '/checkout';
        } catch {
          // Mutation failed (e.g. an add-on variant went out of stock between drawer
          // open and submit). Leave the drawer open so the user can adjust selections
          // and retry. The Skip button (data-action="skip") is the documented escape
          // hatch — it bypasses add-ons and proceeds to /checkout unconditionally.
          // The underlying error is logged by #enqueue (console.error).
          checkoutBtn.removeAttribute('aria-busy');
          checkoutBtn.disabled = false;
        }
        // NOTE: no `finally` re-enable — on success we are navigating away, so
        // re-enabling would cause a brief flash of an interactive button during
        // the navigation. The browser's unload UI is the correct loading state.
      });
    }

    // Wire checkbox change listeners for synchronous running total updates
    this.#items.forEach(item => {
      const cb = item.querySelector('.add-ons-drawer__checkbox');
      if (cb) cb.addEventListener('change', () => this.#updateTotal());
    });

    this.#setupTooltips();
  }

  // Popover API renders the tooltip in the top layer — escapes the
  // drawer body's overflow clipping. JS handles show/hide + positioning
  // because CSS anchor-position isn't universally supported yet.
  #setupTooltips() {
    this.querySelectorAll('.add-ons-drawer__tooltip-trigger').forEach(trigger => {
      const tooltip = trigger.querySelector('.add-ons-drawer__tooltip');
      if (!tooltip) return;

      const show = () => {
        try { tooltip.showPopover(); } catch { return; }
        const t = trigger.getBoundingClientRect();
        const p = tooltip.getBoundingClientRect();
        const left = t.left + t.width / 2 - p.width / 2;
        const top = t.top - p.height - 8;
        tooltip.style.left = `${Math.max(8, Math.min(left, window.innerWidth - p.width - 8))}px`;
        tooltip.style.top = `${top}px`;
      };
      const hide = () => { try { tooltip.hidePopover(); } catch {} };

      trigger.addEventListener('mouseenter', show);
      trigger.addEventListener('mouseleave', hide);
      trigger.addEventListener('focus', show);
      trigger.addEventListener('blur', hide);
    });
  }

  #handleCheckout(event) {
    // Only intercept the checkout button, not other form actions
    // The checkout button has name="checkout" (rendered by snippets/button.liquid)
    if (event.submitter?.name !== 'checkout') return;

    event.preventDefault();
    this.#open(); // async — no need to await from event handler
  }

  async #open() {
    // 1. Fetch live cart state — always fresh (never use mount-time cache)
    const cart = await fetch(`${Shopify.routes.root}cart.js`)
      .then(r => r.json());

    // 2. Build Set of variant IDs already in cart (string comparison)
    const cartVariantIds = new Set(
      cart.items.map(item => String(item.variant_id))
    );

    // 3. Use cached item references (light DOM query won't work after x-drawer opens)
    const items = this.#items;

    // Zero-items guard: if no add-on items in DOM (e.g. all blocks removed/incomplete),
    // skip drawer and proceed directly to checkout
    if (items.length === 0) {
      window.location.href = '/checkout';
      return;
    }

    // 4. Smart-skip (SMRT-02): if every add-on is already in the cart,
    //    redirect immediately to /checkout — do NOT open the drawer.
    const allInCart = items.every(el =>
      cartVariantIds.has(el.dataset.variantId)
    );
    if (allInCart) {
      window.location.href = '/checkout';
      return;
    }

    // 5. Checkbox sync (SMRT-01): pre-check boxes for items already in cart
    items.forEach(item => {
      const checkbox = item.querySelector('.add-ons-drawer__checkbox');
      if (checkbox) {
        checkbox.checked = cartVariantIds.has(item.dataset.variantId);
      }
    });

    // 6. Store cart base total for running total calculations (integer cents)
    this.#cartTotal = cart.total_price;

    // 7. Open the drawer
    if (this.#drawer) {
      this.#drawer.setAttribute('open', '');
    }

    // 8. Display initial running total
    this.#updateTotal();
  }

  #enqueue(fn) {
    // Build a result promise that rejects on error (so caller can catch)
    // but keep #queue always-resolved so future enqueues still execute.
    const result = this.#queue.then(fn);
    this.#queue = result.catch((err) => {
      console.error('[add-ons-drawer] Cart mutation failed:', err);
    });
    return result; // Caller gets the rejecting promise
  }

  async #mutateCart() {
    return this.#enqueue(async () => {
      // 1. Fresh cart fetch — MUST be inside the queued fn to prevent duplicates
      const cart = await fetch(`${Shopify.routes.root}cart.js`).then(r => r.json());
      const cartVariantIds = new Set(cart.items.map(i => String(i.variant_id)));

      // 2. Compute diff from current checkbox state (use cached item refs)
      const items = this.#items;
      const toAdd = items.filter(el =>
        el.querySelector('.add-ons-drawer__checkbox').checked &&
        !cartVariantIds.has(el.dataset.variantId)
      );
      const toRemove = items.filter(el =>
        !el.querySelector('.add-ons-drawer__checkbox').checked &&
        cartVariantIds.has(el.dataset.variantId)
      );

      // 3. Batch add — single POST /cart/add.js with items array (CART-04)
      if (toAdd.length > 0) {
        const addResp = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: toAdd.map(el => ({
              id: parseInt(el.dataset.variantId, 10),
              quantity: 1
            }))
          })
        });
        if (!addResp.ok) {
          const body = await addResp.json().catch(() => ({}));
          throw new Error(`/cart/add.js ${addResp.status}: ${body.description || body.message || addResp.statusText}`);
        }
      }

      // 4. Batch remove — single POST /cart/update.js with updates object using line item KEY (not index)
      if (toRemove.length > 0) {
        const keyByVariant = {};
        cart.items.forEach(item => {
          keyByVariant[String(item.variant_id)] = item.key;
        });
        const updates = {};
        toRemove.forEach(el => {
          const key = keyByVariant[el.dataset.variantId];
          if (key) updates[key] = 0;
        });
        const updateResp = await fetch(`${Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
        if (!updateResp.ok) {
          const body = await updateResp.json().catch(() => ({}));
          throw new Error(`/cart/update.js ${updateResp.status}: ${body.description || body.message || updateResp.statusText}`);
        }
      }
    });
  }

  #close() {
    if (this.#drawer) {
      this.#drawer.removeAttribute('open');
    }
  }

  #updateTotal() {
    // Sum data-price (cents) for all checked add-on items (use cached refs)
    let addOnsTotal = 0;
    this.#items.forEach(item => {
      const cb = item.querySelector('.add-ons-drawer__checkbox');
      if (cb?.checked) {
        addOnsTotal += parseInt(item.dataset.price, 10); // cents
      }
    });

    // Grand total: cart base + selected add-ons (both in cents)
    const grandTotal = this.#cartTotal + addOnsTotal;

    // Format and display — uses Shopify globals (same as Prestige FreeShippingBar)
    const totalEl = this.querySelector('[data-total]');
    if (totalEl) {
      totalEl.textContent = new Intl.NumberFormat(Shopify.locale, {
        style: 'currency',
        currency: Shopify.currency.active
      }).format(grandTotal / 100);
    }
  }
}

customElements.define('add-ons-drawer', AddOnsDrawer);
